import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Empty, Tooltip } from "antd";
import { formatWithCommas } from "../../../assets/helpers";
import "./ActionCodeInsight.scss";

/* How long the highlight lingers after the pointer leaves a point. ApexCharts
   fires dataPointMouseLeave whenever the pointer crosses between markers, so
   clearing immediately makes the list flicker. */
const CLEAR_DELAY = 140;

const HERO_RANKS = 3;

/* ApexCharts centres every x label on its point, so on an area chart the
   first and last labels hang half off the plot and get clipped. Anchoring
   those two inward keeps them whole without reserving a gutter for them.
   Rotated labels are left alone - Apex already end-anchors those. */
const anchorEdgeLabels = (chartContext) => {
  const texts = chartContext?.el?.querySelectorAll(
    ".apexcharts-xaxis-texts-g text",
  );
  if (!texts?.length) return;
  if (/rotate/.test(texts[0].getAttribute("transform") || "")) return;
  texts[0].setAttribute("text-anchor", "start");
  if (texts.length > 1) {
    texts[texts.length - 1].setAttribute("text-anchor", "end");
  }
};

/**
 * Action-code analytics: a gradient distribution curve paired with a summary
 * block and a ranked, scrollable code list. Hovering either the curve or a row
 * highlights the same code in both.
 *
 * The codes are sorted high to low, so the line reads as a distribution curve -
 * how steeply outcomes fall away from the leading code - rather than a line
 * drawn through categories in arbitrary order.
 */
const ActionCodeInsight = ({
  codes = [],
  total = 0,
  palette = [],
  countLabel = "Calls",
  accent = "#ff6843",
  accentSoft = "#ffb49c",
  height = 300,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const clearTimer = useRef(null);

  const rows = useMemo(
    () =>
      (codes || [])
        .map((c, i) => ({
          code: c?.code ?? "-",
          label: c?.label ?? c?.code ?? "-",
          count: Number(c?.count) || 0,
          percent: Number(c?.percent) || 0,
          color: palette[i % palette.length],
        }))
        .sort((a, b) => b.count - a.count),
    [codes, palette],
  );

  const maxCount = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.count), 0),
    [rows],
  );

  /* How concentrated the outcomes are - the read a long list makes you work
     out for yourself. */
  const concentration = useMemo(() => {
    const ranks = Math.min(HERO_RANKS, rows.length);
    const sum = rows.slice(0, ranks).reduce((a, r) => a + r.count, 0);
    const base = rows.reduce((a, r) => a + r.count, 0);
    return { ranks, percent: base ? Math.round((sum / base) * 100) : 0 };
  }, [rows]);

  /* --------------------------- hover coordination -------------------------- */

  const cancelClear = () => {
    if (clearTimer.current) {
      clearTimeout(clearTimer.current);
      clearTimer.current = null;
    }
  };

  const highlight = useCallback((index) => {
    cancelClear();
    setActiveIndex((current) => (current === index ? current : index));
  }, []);

  const scheduleClear = useCallback(() => {
    cancelClear();
    clearTimer.current = setTimeout(() => {
      clearTimer.current = null;
      setActiveIndex(null);
    }, CLEAR_DELAY);
  }, []);

  useEffect(() => cancelClear, []);

  /* Drop a stale highlight when the underlying dataset changes. */
  useEffect(() => {
    setActiveIndex((current) =>
      current != null && current >= rows.length ? null : current,
    );
  }, [rows.length]);

  /* ---------------------------- distribution curve -------------------------- */

  const areaOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        fontFamily: "Gilroy, Inter, sans-serif",
        parentHeightOffset: 0,
        zoom: { enabled: false },
        animations: { easing: "easeinout", speed: 500 },
        events: {
          mounted: anchorEdgeLabels,
          updated: anchorEdgeLabels,
          dataPointMouseEnter: (e, ctx, cfg) => highlight(cfg.dataPointIndex),
          dataPointMouseLeave: () => scheduleClear(),
          mouseLeave: () => scheduleClear(),
        },
      },
      colors: [accent],
      stroke: { curve: "smooth", width: 3, lineCap: "round" },
      /* the fill carries the channel's own accent, fading out to the surface */
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 1,
          gradientToColors: [accentSoft],
          opacityFrom: 0.38,
          opacityTo: 0.02,
          stops: [0, 92, 100],
        },
      },
      dataLabels: { enabled: false },
      markers: {
        size: rows.length > 14 ? 0 : 4,
        colors: ["#ffffff"],
        strokeColors: accent,
        strokeWidth: 2,
        hover: { size: 6, sizeOffset: 0 },
      },
      xaxis: {
        categories: rows.map((r) => r.code),
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        crosshairs: {
          show: true,
          stroke: { color: accentSoft, width: 1, dashArray: 4 },
        },
        labels: {
          rotate: -40,
          rotateAlways: rows.length > 8,
          hideOverlappingLabels: true,
          trim: true,
          style: {
            colors: "#838a9e",
            fontSize: "10.5px",
            fontWeight: 600,
            fontFamily: "Gilroy, Inter, sans-serif",
          },
          formatter: (val) =>
            val && val.length > 9 ? val.slice(0, 9) + "..." : val,
        },
      },
      yaxis: {
        min: 0,
        /* Counts are whole numbers: with a small max Apex picks ticks like
           2.5 and 1.5, which the rounding formatter printed as repeated
           "3 3 2 2 1 1". One tick per unit keeps the axis honest. */
        tickAmount: maxCount > 0 && maxCount <= 5 ? maxCount : undefined,
        labels: {
          /* Apex pads 10px to the left of the y labels; pull them back to
             the panel edge so the chart lines up with the heading above. */
          offsetX: -16,
          style: {
            colors: "#a3a9bb",
            fontSize: "11px",
            fontWeight: 600,
            fontFamily: "Gilroy, Inter, sans-serif",
          },
          formatter: (v) => formatWithCommas(Math.round(v)),
        },
      },
      grid: {
        borderColor: "#eff1f8",
        strokeDashArray: 5,
        xaxis: { lines: { show: false } },
        padding: { left: 2, right: 8, top: -8 },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex }) => {
          const row = rows[dataPointIndex] || {};
          const value = series[seriesIndex][dataPointIndex] || 0;
          return (
            '<div class="dsh-chart-tip">' +
            '<div class="dsh-chart-tip__head">' +
            '<span class="dsh-chart-tip__dot" style="background:' +
            accent +
            '"></span>' +
            (row.label || "") +
            "</div>" +
            '<div class="dsh-chart-tip__row"><span>' +
            countLabel +
            "</span><b>" +
            formatWithCommas(value) +
            "</b></div>" +
            '<div class="dsh-chart-tip__row"><span>Share</span><b>' +
            (row.percent || 0) +
            "%</b></div>" +
            "</div>"
          );
        },
      },
    }),
    [rows, maxCount, accent, accentSoft, countLabel, highlight, scheduleClear],
  );

  const areaSeries = useMemo(
    () => [{ name: countLabel, data: rows.map((r) => r.count) }],
    [rows, countLabel],
  );

  /* Every code at zero drew a flat line pinned to the axis with an invented
     0-2 scale above it, and a list of zeros beside it - a lot of chrome for
     "nothing happened". */
  const hasVolume = rows.some((r) => r.count > 0);

  if (!rows.length || !hasVolume) {
    return (
      <div
        className="dsh-insight dsh-insight--empty"
        style={{ minHeight: height }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            rows.length
              ? `No ${countLabel.toLowerCase()} recorded for these filters`
              : "No action codes yet"
          }
        />
      </div>
    );
  }

  const active = activeIndex != null ? rows[activeIndex] : null;
  const topRow = rows[0];

  return (
    <div className="dsh-insight">
      <div className="dsh-insight__chart">
        <div className="dsh-insight__chart-head">
          <span className="dsh-insight__eyebrow">Distribution</span>
          <span className="dsh-insight__chart-hint">
            {rows.length} codes · {countLabel.toLowerCase()}
          </span>
        </div>
        <ReactApexChart
          options={areaOptions}
          series={areaSeries}
          type="area"
          height={height}
        />
      </div>

      <div className="dsh-insight__side" style={{ maxHeight: height + 32 }}>
        {/* A pie of a dozen-plus codes is unreadable, and it repeated the
            shares the list below already gives. These are the figures you
            cannot read off that list at a glance. */}
        <div className="dsh-insight__summary">
          <div className="dsh-insight__summary-hero">
            <span className="dsh-insight__summary-label">
              {active ? active.label : `Total ${countLabel}`}
            </span>
            <b className="dsh-insight__summary-value">
              {active
                ? `${formatWithCommas(active.count)}`
                : formatWithCommas(total || 0)}
            </b>
            {active && (
              <span className="dsh-insight__summary-sub">
                {active.percent}% of all {countLabel.toLowerCase()}
              </span>
            )}
          </div>

          <div className="dsh-insight__summary-row">
            <span className="dsh-insight__summary-key">Most common</span>
            <Tooltip title={topRow.label}>
              <span className="dsh-insight__summary-val">{topRow.label}</span>
            </Tooltip>
            <span className="dsh-insight__summary-pct">{topRow.percent}%</span>
          </div>

          <div className="dsh-insight__summary-row is-block">
            <span className="dsh-insight__summary-key">
              Top {concentration.ranks} cover
            </span>
            <span className="dsh-insight__summary-pct">
              {concentration.percent}%
            </span>
            <span className="dsh-insight__spine" aria-hidden="true">
              <i
                style={{
                  width: `${concentration.percent}%`,
                  background: accent,
                }}
              />
            </span>
          </div>
        </div>

        <ul className="dsh-insight__list">
          {rows.map((r, i) => (
            <li
              key={`${r.code}-${i}`}
              className={activeIndex === i ? "is-active" : ""}
              onMouseEnter={() => highlight(i)}
              onMouseLeave={scheduleClear}
            >
              <span
                className="dsh-insight__swatch"
                style={{ background: r.color }}
              />
              <Tooltip title={r.label}>
                <span className="dsh-insight__name">{r.label}</span>
              </Tooltip>
              <span className="dsh-insight__count">
                {formatWithCommas(r.count)}
              </span>
              <span className="dsh-insight__meter">
                <i
                  style={{
                    width: `${maxCount ? (r.count / maxCount) * 100 : 0}%`,
                    background: r.color,
                  }}
                />
              </span>
              <span className="dsh-insight__pct">{r.percent}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ActionCodeInsight;
